import React from "react";
import { Mail, MessageCircle, Phone } from "lucide-react";
import { FOOTER } from "@/constants/testIds";
import { track } from "@/lib/analytics";
import {
  UK_OFFICE_PHONE_RAW,
  UK_OFFICE_PHONE_DISPLAY,
} from "@/lib/contact";

const CONTACT_EMAIL = "admin@heartreachcounselling.com";
const WHATSAPP_DISPLAY = "+263 78 654 3884";
const WHATSAPP_HREF = "https://wa.me/263786543884";

export const Footer = ({ onOpenPrivacy, onOpenCookies, onOpenAbout, onOpenSponsor }) => {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid={FOOTER.root}
      className="mt-10 bg-white border-t border-[#E8E2D4]"
    >
      <div className="max-w-2xl mx-auto px-6 sm:px-8 py-10 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
          {/* Contact */}
          <div>
            <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0E7C7B]">
              Contact us
            </p>
            <h3 className="font-serif text-xl text-[#1F2E4D] mt-2 leading-snug">
              We're here to listen.
            </h3>

            <div className="mt-5 flex flex-col gap-3">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid={FOOTER.emailLink}
                onClick={() => track("contact_email_clicked")}
                className="inline-flex items-center gap-3 text-[15px] text-[#1F2E4D] hover:text-[#0E7C7B] transition-colors"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#E0EBEB] shrink-0">
                  <Mail className="w-4 h-4 text-[#0E7C7B]" aria-hidden />
                </span>
                <span className="font-medium break-all">{CONTACT_EMAIL}</span>
              </a>
              <a
                href={WHATSAPP_HREF}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={FOOTER.whatsappLink}
                onClick={() => track("contact_whatsapp_clicked")}
                className="inline-flex items-center gap-3 text-[15px] text-[#1F2E4D] hover:text-[#0E7C7B] transition-colors"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#E0EBEB] shrink-0">
                  <MessageCircle className="w-4 h-4 text-[#0E7C7B]" aria-hidden />
                </span>
                <span className="font-medium">WhatsApp&nbsp;·&nbsp;{WHATSAPP_DISPLAY}</span>
              </a>
              <a
                href={`tel:${UK_OFFICE_PHONE_RAW}`}
                data-testid="footer-uk-office-link"
                onClick={() => track("contact_uk_office_clicked")}
                className="inline-flex items-center gap-3 text-[15px] text-[#1F2E4D] hover:text-[#0E7C7B] transition-colors"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#E0EBEB] shrink-0">
                  <Phone className="w-4 h-4 text-[#0E7C7B]" aria-hidden />
                </span>
                <span className="font-medium">UK office&nbsp;·&nbsp;{UK_OFFICE_PHONE_DISPLAY}</span>
              </a>
            </div>
          </div>

          {/* Legal */}
          <div>
            <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0E7C7B]">
              Your privacy
            </p>
            <h3 className="font-serif text-xl text-[#1F2E4D] mt-2 leading-snug">
              Compliant on both ends.
            </h3>
            <p className="text-sm text-[#4A5568] mt-3 leading-relaxed">
              We operate under UK GDPR and Zimbabwe's Cyber and Data
              Protection Act (2021).
            </p>
            <div className="mt-5 flex flex-col gap-2 text-[15px]">
              {onOpenSponsor && (
                <button
                  type="button"
                  onClick={() => { track("nav_footer_sponsor_clicked"); onOpenSponsor?.(); }}
                  data-testid="footer-sponsor-link"
                  className="text-left text-[#1F2E4D] hover:text-[#0E7C7B] underline-offset-4 hover:underline transition-colors"
                >
                  Sponsor a relative
                </button>
              )}
              {onOpenAbout && (
                <button
                  type="button"
                  onClick={() => { track("nav_footer_about_clicked"); onOpenAbout?.(); }}
                  data-testid="footer-about-link"
                  className="text-left text-[#1F2E4D] hover:text-[#0E7C7B] underline-offset-4 hover:underline transition-colors"
                >
                  About us
                </button>
              )}
              <button
                type="button"
                onClick={() => { track("privacy_policy_opened"); onOpenPrivacy?.(); }}
                data-testid={FOOTER.privacyLink}
                className="text-left text-[#1F2E4D] hover:text-[#0E7C7B] underline-offset-4 hover:underline transition-colors"
              >
                Privacy Policy
              </button>
              <button
                type="button"
                onClick={() => { track("cookies_policy_opened"); onOpenCookies?.(); }}
                data-testid={FOOTER.cookiesLink}
                className="text-left text-[#1F2E4D] hover:text-[#0E7C7B] underline-offset-4 hover:underline transition-colors"
              >
                Cookies Policy
              </button>
            </div>
          </div>
        </div>

        <hr className="hr-divider my-10" />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-[#4A5568]">
          <p>
            © {year} Heart Reach Counselling Services. All rights reserved.
          </p>
          <p className="italic">We listen to understand.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
